<template>
  <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 w-9">
    <div class="flex-shrink-0">
      <img v-if="customer.attributes.avatar" class="h-12 w-12 rounded-full object-cover" :src="customer.attributes.avatar" alt="" />
      <svg v-else class="bg-gray-100 rounded-full p-2" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="48px" height="48px" viewBox="0 0 24 24" version="1.1">
        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <polygon points="0 0 24 0 24 24 0 24"/>
            <path d="M12,11 C9.790861,11 8,9.209139 8,7 C8,4.790861 9.790861,3 12,3 C14.209139,3 16,4.790861 16,7 C16,9.209139 14.209139,11 12,11 Z" fill="#000000" fill-rule="nonzero" opacity="0.3"/>
            <path d="M3.00065168,20.1992055 C3.38825852,15.4265159 7.26191235,13 11.9833413,13 C16.7712164,13 20.7048837,15.2931929 20.9979143,20.2 C21.0095879,20.3954741 20.9979143,21 20.2466999,21 C16.541124,21 11.0347247,21 3.72750223,21 C3.47671215,21 2.97953825,20.45918 3.00065168,20.1992055 Z" fill="#000000" fill-rule="nonzero"/>
        </g>
      </svg>
    </div>
  </td>
  <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
    {{ customer.attributes.name }}
  </td>
  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 ">
    {{parsedPhoneNumber}}
  </td>
  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 ">
    {{ customer.attributes['email'] }}
  </td>
  <td class="px-3 py-4 text-sm text-gray-500 sm:max-w-xs lg:max-w-lg truncate">
    <VLink v-if="conversation" v-bind:href="conversationUrl">
      <button class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 mr-3"
              :class="{'bg-white': conversation.status === 'new', 'bg-gray-50': conversation.status === 'assigned', 'bg-red-50': conversation.status === 'closed'}">
        <ChatAltIcon class="-ml-1 mr-2 h-5 w-5" /> {{ $t('customers.toConversation') }}
      </button>
    </VLink>
    <div v-else>
      <button @click="toggleCreateConversation" v-if="canCreateTemplatedConversation" class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 mr-3">
        <ChatAltIcon class="-ml-1 mr-2 h-5 w-5" /> {{ $t('customers.createConversation') }}
      </button>
    </div>
  </td>
  <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
    <button v-on:click="toggleEdit" class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 mr-3">
      <PencilIcon class="-ml-1 mr-2 h-5 w-5" /> {{ $t('actions.edit') }}
    </button>
    <div v-if="this.$root.SocketsReady && !this.$root.SocketDisconnected && conversation" class="inline">
      <button v-if="!this.sourceMerge || selectedAsSource" v-on:click="selectMergeSource" class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 mr-3">
        <XIcon v-if="selectedAsSource" class="-ml-1 mr-2 h-5 w-5" />
        <LinkIcon v-else class="-ml-1 mr-2 h-5 w-5" />
        <span v-if="selectedAsSource">{{ $t('actions.cancel') }}</span>
        <span v-else>{{ $t('actions.merge') }}</span>
      </button>
      <button v-if="showAsValidTargetForMerge" v-on:click="selectMergeTarget" class="inline-flex items-center px-4 py-2 border bg-indigo-600 border-indigo-600 rounded-md shadow-sm text-sm font-medium hover:bg-indigo-700 mr-3 text-white">
        <LinkIcon class="-ml-1 mr-2 h-5 w-5" />
        {{ $t('actions.merge') }}
      </button>
    </div>
    <CustomerEdit v-if="edit" v-bind:customer="customer" v-on:update-finished-event="toggleEdit" />
    <ConversationCreate v-if="showCreateTemplatedConversation" v-bind:customer="customer" v-bind:integration="vonageIntegration" v-on:create-finished-event="conversationCreated" />
  </td>
</template>


<script>
import CustomerEdit from './CustomerEdit';
import ConversationCreate from './ConversationCreate';
import { PencilIcon, LinkIcon, XIcon } from '@heroicons/vue/solid';
import { ChatAltIcon } from '@heroicons/vue/outline';
import VLink from '../page/VLink.vue'
import { useConversationsStore } from "@/stores/conversations";
import { useVonageStore } from "@/stores/vonageIntegrations";
import parsePhoneNumber from 'libphonenumber-js';


export default {
  name: 'CustomerListItem',
  props: ['customer', 'sourceMerge', 'targetMerge'],
  emits: ['merge-source-selected-event', 'merge-target-selected-event'],
  components: {
    CustomerEdit,
    VLink,
    ConversationCreate,
    //
    PencilIcon,
    LinkIcon,
    XIcon,
    ChatAltIcon,
  },
  setup() {
    let conversationsStore = useConversationsStore();
    let vonageStore = useVonageStore();
    return { conversationsStore, vonageStore };
  },
  methods: {
    toggleEdit() {
      this.edit = !this.edit;
    },
    selectMergeSource() {
      if (this.selectedAsSource) {
        this.$emit('merge-source-selected-event', undefined);
      } else {
        this.$emit('merge-source-selected-event', this.customer);
      }
    },
    selectMergeTarget() {
      this.$emit('merge-target-selected-event', this.customer);
    },
    toggleCreateConversation() {
      this.showCreateTemplatedConversation = true;
    },
    conversationCreated() {
      this.showCreateTemplatedConversation = false;
    },
  },
  computed: {
    selectedAsSource() {
      return this.sourceMerge && this.sourceMerge.id === this.customer.id;
    },
    showAsValidTargetForMerge() {
      if (!this.sourceMerge) {
        return false;
      }
      return !this.selectedAsSource;
    },
    conversationUrl() {
      return `/conversations/${this.conversation.id}`;
    },
    conversation() {
      return this.conversationsStore.getByCustomerId(this.customer.id)[0];
    },
    canCreateTemplatedConversation() {
      let vonage = this.vonageIntegration;
      let hasVonage = vonage && vonage.attributes.active;
      return this.customer.attributes['phone-number'] && hasVonage;
    },
    vonageIntegration() {
      if (this.vonageStore.collection.length > 0) {
        return this.vonageStore.collection[0];
      }
      return undefined;
    },
    parsedPhoneNumber() {
      let parsed;
      let phone = this.customer.attributes['phone-number'];
      if (phone) {
        if (phone.indexOf('+') === -1) {
          parsed = parsePhoneNumber(`+${phone}`);
        } else {
          parsed = parsePhoneNumber(phone);
        }
        if (parsed && parsed.isValid()) {
          return parsed.number;
        } else {
          return phone;
        }
      }
      return '';
    }
  },
  data() {
    return {
      edit: false,
      name: this.customer.attributes.name,
      showCreateTemplatedConversation: false,
    }
  },
}
</script>
